import * as React from 'react'
import { Stethoscope, Package, Tag, DollarSign, Hash, FileText, Loader2, Calendar, User } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useTreatment, useTreatmentInvoices } from '@/hooks/useTreatments'
import { formatCurrency, formatNumber } from '@/lib/utils'
import { CATEGORY_COLORS } from './treatment-columns'
import type { TreatmentType } from '@/lib/api-client'

interface TreatmentSheetProps {
  treatmentId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const TYPE_COLORS: Record<TreatmentType, string> = {
  treatment: '#3b82f6',
  product: '#10b981',
  medication: '#f97316',
}

const TYPE_LABELS: Record<TreatmentType, string> = {
  treatment: 'Treatment',
  product: 'Product',
  medication: 'Medication',
}

export function TreatmentSheet({
  treatmentId,
  open,
  onOpenChange,
}: TreatmentSheetProps) {
  const { data: treatment, isLoading } = useTreatment(treatmentId)
  const [invoicePage, setInvoicePage] = React.useState(1)

  const { data: invoicesData, isLoading: invoicesLoading } = useTreatmentInvoices(
    treatmentId,
    { page: invoicePage, pageSize: 10 }
  )

  // Reset page when treatment changes
  React.useEffect(() => {
    setInvoicePage(1)
  }, [treatmentId])

  const categoryColor = treatment?.categoryName
    ? CATEGORY_COLORS[treatment.categoryName] || '#6b7280'
    : '#6b7280'

  const typeColor = treatment?.type ? TYPE_COLORS[treatment.type] : '#6b7280'
  const typeLabel = treatment?.type ? TYPE_LABELS[treatment.type] : 'Unknown'

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-MY', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5" />
            {isLoading ? (
              <Skeleton className="h-6 w-48" />
            ) : (
              treatment?.name || 'Treatment Details'
            )}
          </SheetTitle>
          <SheetDescription className="flex items-center gap-2 flex-wrap">
            {isLoading ? (
              <Skeleton className="h-5 w-32" />
            ) : (
              <>
                <Badge style={{ background: categoryColor }} className="text-white text-xs">
                  {treatment?.categoryName || 'Uncategorized'}
                </Badge>
                <Badge style={{ background: typeColor }} className="text-white text-xs">
                  {typeLabel}
                </Badge>
                <Badge
                  className="text-white text-xs"
                  style={{ background: treatment?.isActive ? '#22c55e' : '#ef4444' }}
                >
                  {treatment?.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </>
            )}
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="space-y-6 mt-6">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : treatment ? (
          <div className="space-y-6 mt-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-muted p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Hash className="h-4 w-4" />
                  <span className="text-xs">Total Sales</span>
                </div>
                <div className="text-2xl font-bold">{formatNumber(treatment.itemCount)}</div>
              </div>
              <div className="rounded-lg bg-muted p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <DollarSign className="h-4 w-4" />
                  <span className="text-xs">Total Revenue</span>
                </div>
                <div className="text-2xl font-bold">{formatCurrency(treatment.revenue)}</div>
              </div>
              <div className="rounded-lg bg-muted p-4 col-span-2">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <DollarSign className="h-4 w-4" />
                  <span className="text-xs">Average per Sale</span>
                </div>
                <div className="text-2xl font-bold">
                  {treatment.itemCount > 0
                    ? formatCurrency(treatment.revenue / treatment.itemCount)
                    : 'RM 0.00'}
                </div>
              </div>
            </div>

            {/* Treatment Info */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Treatment Info</h3>
              <div className="space-y-3">
                <InfoRow icon={Tag} label="Category" value={treatment.categoryName || '-'} />
                <InfoRow icon={Package} label="Type" value={typeLabel} />
                {treatment.basePrice && (
                  <InfoRow icon={DollarSign} label="Base Price" value={formatCurrency(treatment.basePrice)} />
                )}
                {treatment.description && (
                  <InfoRow icon={Stethoscope} label="Description" value={treatment.description} />
                )}
              </div>

              {/* Aliases */}
              {treatment.aliases && treatment.aliases.length > 0 && (
                <div className="pt-2">
                  <div className="text-xs text-muted-foreground mb-2">Aliases</div>
                  <div className="flex flex-wrap gap-2">
                    {treatment.aliases.map((alias, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {alias}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Recent Invoices */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">
                  Recent Invoices ({invoicesData?.total ?? 0})
                </h3>
              </div>

              {/* Invoices list */}
              {invoicesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : invoicesData && invoicesData.data.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {invoicesData.data.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-start justify-between rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="font-medium text-sm">{invoice.invoiceNo}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <User className="h-3 w-3" />
                          <span className="truncate">{invoice.patientName || 'Unknown'}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(invoice.invoiceDate)}</span>
                          <span className="text-text-muted">•</span>
                          <span>{invoice.branchName}</span>
                        </div>
                      </div>
                      <div className="text-right ml-3">
                        <div className="font-medium text-sm">
                          {formatCurrency(invoice.itemTotal)}
                        </div>
                        <Badge
                          className="text-xs mt-1"
                          style={{
                            background: invoice.patientType === 'New' ? '#3b82f6' : '#22c55e',
                            color: 'white'
                          }}
                        >
                          {invoice.patientType}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No invoices found for this treatment
                </div>
              )}

              {/* Pagination for invoices */}
              {invoicesData && invoicesData.total > 10 && (
                <div className="flex items-center justify-between pt-2">
                  <span className="text-xs text-muted-foreground">
                    Showing {Math.min(invoicePage * 10, invoicesData.total)} of {invoicesData.total}
                  </span>
                  <div className="flex items-center gap-2">
                    {invoicePage > 1 && (
                      <button
                        onClick={() => setInvoicePage(p => p - 1)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Previous
                      </button>
                    )}
                    {invoicePage * 10 < invoicesData.total && (
                      <button
                        onClick={() => setInvoicePage(p => p + 1)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Load more
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Treatment not found
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground" />
      <div className="flex-1">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm">{value}</div>
      </div>
    </div>
  )
}
